/**
 * The single app-wide announcement vehicle (§12). One Snackbar visible at a
 * time; further calls queue and show in order. Inverse-surface tonal, corner-sm,
 * pinned bottom-inline-start, auto-dismisses after 4s. The container is a
 * persistent `role="status" aria-live="polite"` region, so every message —
 * resolved, claimed, copied, send-failed — is announced through this one region
 * (the Phase 5 bespoke copy live-region was removed to avoid double announcements).
 *
 * `useSnackbar()` returns a no-op when no provider is mounted, so a component can
 * be unit-tested in isolation without wrapping it in <SnackbarProvider>.
 */

import {
  createContext, useCallback, useContext, useEffect, useRef, useState,
  type ReactNode,
} from 'react';

export interface SnackbarAction {
  label: string;
  onClick: () => void;
}
export interface SnackbarOptions {
  message: string;
  action?: SnackbarAction;
}
interface SnackItem extends SnackbarOptions {
  id: number;
}
interface SnackbarApi {
  show: (opts: SnackbarOptions) => void;
}

const SnackbarContext = createContext<SnackbarApi>({ show: () => {} });

export function useSnackbar(): SnackbarApi {
  return useContext(SnackbarContext);
}

const DURATION_MS = 4000;

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<SnackItem[]>([]);
  const idRef = useRef(0);
  const current = queue[0] ?? null;
  const currentId = current?.id;

  const show = useCallback((opts: SnackbarOptions) => {
    idRef.current += 1;
    setQueue((q) => [...q, { ...opts, id: idRef.current }]);
  }, []);

  const dismiss = useCallback(() => setQueue((q) => q.slice(1)), []);

  // Auto-dismiss the visible snackbar; the timer restarts for each distinct id.
  useEffect(() => {
    if (currentId === undefined) return;
    const timer = setTimeout(() => setQueue((q) => q.slice(1)), DURATION_MS);
    return () => clearTimeout(timer);
  }, [currentId]);

  return (
    <SnackbarContext.Provider value={{ show }}>
      {children}
      {/* Persistent live region (kept mounted so aria-live announces reliably). */}
      <div
        role="status"
        aria-live="polite"
        className="fixed z-[60] bottom-4 start-4 flex flex-col pointer-events-none"
      >
        {current && (
          <div
            key={current.id}
            className="snackbar-enter pointer-events-auto flex items-center gap-2
              min-w-[280px] max-w-[min(92vw,420px)] ps-4 pe-2 py-3 rounded-corner-sm
              bg-inverse-surface text-inverse-on-surface shadow-elevation-3"
          >
            <span className="flex-1 text-body-medium">{current.message}</span>
            {current.action && (
              <button
                type="button"
                onClick={() => { current.action!.onClick(); dismiss(); }}
                className="shrink-0 h-9 px-3 rounded-corner-full text-label-large text-inverse-primary
                  hover:bg-inverse-on-surface/[0.12] transition-colors duration-m3-short
                  focus-visible:outline-2 focus-visible:outline-inverse-primary focus-visible:outline-offset-2"
              >
                {current.action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </SnackbarContext.Provider>
  );
}
